<template>
  <div class="review-slide" :class="wrapperClasses">
    <h3 v-if="!isInterlude" class="slide-num ref-num">
      <span class="toggle-controls" @click="toggleControls">
        <b-icon icon="settings" size="is-medium" />
      </span>
      <span class="current bigger highlight">{{slideNumber}}</span>
      <span class="total highlight" :title="subIndexInfo">{{total}}</span>
      <strong class="uid middle">{{uid}}</strong>
      <strong class="work-num">{{workNumber}}</strong>
      <span v-if="showName" class="artist-name">{{artistName}}</span>
    </h3>
    <media-card v-if="isMedia" :item="slide" :work="slide.work" :details="slide.submission" :auto="true"
      :active="active" :showExtra="false" />
    <div v-if="isText" class="proposal-text" v-html="description"></div>
    <h2 v-if="isInterlude" class="artist-intro">
      <span class="text" v-html="largeLabel" :title="comboInfo"></span>
      <h6 v-if="showName" class="artist-name">{{artistName}}</h6>
      <h6 v-if="showName && hasLocality" class="locality">{{locality}}</h6>
      <div v-if="isIntro" class="extra-info">
        <!-- <h4 class="works-info">
          <span class="num-works">{{worksInfo}}</span>
          <span class="num-slides">{{slidesInfo}}</span>
        </h4>
        <h5 class="medium-info">{{mediumInfo}}</h5> -->
        <b-icon v-if="longRead" icon="book-open-variant" size="is-large" />
        <!-- <p v-if="hasEducation" class="education college">{{college}}</p>
        <p v-if="hasCourse" class="education course">{{course}}</p> -->
      </div>
    </h2>
    <div v-if="mayMark" class="slide-controls">
      <!-- <span
        class="update"
        :class="longRead|statusClass"
        @click="update('longRead')"
        title="Long read"
      >
        <b-icon icon="book-open-variant" size="is-medium" />
        <em>Long read</em>
      </span> -->
      <span class="update" :class="shortlisted|statusClass" @click="update('shortlisted')" title="Return to">
        <b-icon icon="shield-check-outline" size="is-medium" />
        <em>Return to</em>
      </span>
      <span v-if="shortlisted" class="update" :class="selected|statusClass" @click="update('selected')"
        title="Shortlist work">
        <b-icon icon="license" size="is-medium" />
        <em>Shortlist work</em>
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import filters from "../mixins/filters";
import { slideSchema } from "../store/schemas";
import MediaCard from "./MediaCard";
import { reviewSubmission } from "../api/methods";
import { extractNestedOptions, notEmptyString } from "../api/helpers";
import { bus } from "../main";

export default {
  name: "ReviewSlide",
  components: {
    MediaCard
  },
  props: {
    slide: {
      type: Object,
      default() {
        return slideSchema;
      }
    },
    total: {
      type: Number,
      default: 0
    },
    active: {
      type: Boolean,
      default: false
    },
    mayMark: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      showControls: false,
      mediaLabels: [],
      showName: true
    };
  },
  mixins: [filters],
  created() {
    this.mediaLabels = extractNestedOptions("work", "medium", this.options);
  },
  computed: {
    ...mapGetters(["settings", "options"]),
    wrapperClasses() {
      const activeClass = this.active ? "active" : "inactive";
      const [subIndex, workIndex, mediaIndex] = this.slide.indices;
      let cls = [
        activeClass,
        this.slide.type,
        "index-" + this.slide.index,
        "sub-index-" + subIndex,
        "work-index-" + workIndex,
        "media-index-" + mediaIndex
      ];
      const { submission } = this.slide;
      if (submission.checked) {
        cls.push("checked");
      }
      if (submission.marked) {
        cls.push("marked");
      }
      if (submission.shortlisted) {
        cls.push("shortlisted");
      }
      if (this.showControls) {
        cls.push("show-controls");
      }
      return cls;
    },
    type() {
      return this.slide.type;
    },
    artistName() {
      const { fullName, collectiveName} = this.slide.submission
      if (notEmptyString(collectiveName)) {
        return collectiveName;
      } else {
        return fullName;
      }
    },
    locality() {
      const { locality } = this.slide.submission;
      if (locality) {
        return locality;
      }
      return "";
    },
    hasLocality() {
      const { locality } = this.slide.submission;
      if (notEmptyString(locality, 1)) {
        return true;
      }
      return false;
    },
    statement() {
      const { statement } = this.slide.submission;
      if (notEmptyString(statement, 1)) {
        return statement;
      }
      return "";
    },
    hasStatement() {
      const { statement } = this.slide.submission;
      if (notEmptyString(statement, 1)) {
        return true;
      }
      return false;
    },
    college() {
      return this.slide.submission.college;
    },
    course() {
      return this.slide.submission.course;
    },
    hasEducation() {
      return notEmptyString(this.college);
    },
    hasCourse() {
      return notEmptyString(this.course);
    },
    isText() {
      return this.type === "text";
    },
    isMedia() {
      return this.type !== "text";
    },
    description() {
      return this.slide.work.description;
    },
    numSlides() {
      return this.slide.numSlides;
    },
    numWorks() {
      return this.slide.numWorks;
    },
    numSubmissions() {
      return this.slide.numSubmissions;
    },
    slideNumber() {
      return this.slide.slideNum;
    },
    subNumber() {
      return this.matchSectionNumber(0);
    },
    workNumber() {
      return this.matchSectionNumber(1);
    },
    mediaNumber() {
      return this.matchSectionNumber(2);
    },
    uid() {
      return this.slide.submission.uid;
    },
    longRead() {
      return this.slide.submission.longRead;
    },
    returnTo() {
      return this.slide.submission.returnTo;
    },
    shortlisted() {
      return this.slide.submission.shortlisted;
    },
    selected() {
      return this.slide.work.selected;
    },
    isIntro() {
      return this.slide.type === "intro";
    },
    isEnd() {
      return this.slide.type === "end";
    },
    isInterlude() {
      return ["intro", "end"].indexOf(this.slide.type) >= 0;
    },
    largeLabel() {
      let str = "";
      if (this.isIntro) {
        str = this.artistIntroLabel;
      } else if (this.isEnd) {
        str = this.slide.title;
      }
      return str;
    },
    artistIntroLabel() {
      const subIndex = this.slide.indices[0];
      let labelWords = [];
      const { path } = this.$route;
      let stage = "valid";
      const parts = notEmptyString(path, 4) ? path.substring(1).split("/") : [];
      if (parts.length > 1) {
        stage = parts[1];
      }
      if (stage !== "user") {
        if (subIndex < 1) {
          labelWords.push("First");
        } else {
          labelWords.push("Next");
        }
      }
      let artistTerm = labelWords.length > 1 ? "artist" : "Artist";
      labelWords.push(artistTerm);
      labelWords.push("#" + this.slide.submission.uid);
      return labelWords.join(" ");
    },
    worksInfo() {
      let labelWords = [];
      labelWords.push(this.slide.numWorks);
      if (this.slide.numWorks > 1) {
        labelWords.push("works");
      } else {
        labelWords.push("work");
      }
      return labelWords.join(" ");
    },
    mediumInfo() {
      let out = "";
      if (notEmptyString(this.slide.sourceFilename, 3)) {
        const mediaTypes = this.slide.sourceFilename.split(",");
        const mediaLbls = [];
        if (mediaTypes.length > 0) {
          mediaTypes.forEach(mt => {
            let lbl = mt.replace(/_+/g, " ");
            const item = this.mediaLabels.find(ml => {
              return ml.key.toString() === mt.trim();
            });
            if (item) {
              lbl = item.value.replace(/_+/g, " ");
            }
            if (mediaLbls.indexOf(lbl) < 0) {
              mediaLbls.push(lbl);
            }
          });
          if (mediaLbls.length > 0) {
            out = "(" + mediaLbls.join(", ") + ")";
          }
        }
      }
      return out;
    },
    slidesInfo() {
      let labelWords = [];
      labelWords.push(this.slide.numSlides);
      if (this.slide.numSlides > 1) {
        labelWords.push("slides");
      } else {
        labelWords.push("slide");
      }
      return labelWords.join(" ");
    },
    comboInfo() {
      return [this.slidesInfo, this.worksInfo].join(", ");
    },
    eduInfo() {
      const parts = [];
      if (this.hasEducation) {
        parts.push(this.college)
      }
      if (this.hasCourse) {
        parts.push(this.course)
      }
      return parts.join(" / ");
    },
    subIndexInfo() {
      return [this.subNumber, this.workNumber, this.mediaNumber].join(":");
    }
  },
  filters: {
    statusClass(val) {
      return val ? "on" : "off";
    }
  },
  methods: {
    matchSectionNumber(index = 0) {
      let val = 1;
      if (
        this.slide.indices instanceof Array &&
        this.slide.indices.length > index
      ) {
        val = this.slide.indices[index] + 1;
      }
      return val;
    },
    toggleControls() {
      this.showControls = !this.showControls;
    },
    update(key) {
      const { submission, work } = this.slide;
      const { _id } = submission;
      let workId = null;
      if (key === "selected") {
        workId = work._id;
      }
      let newVal = false;
      let valid = false;
      switch (key) {
        case "returnTo":
          newVal = submission.returnTo === false;
          valid = true;
          break;
        case "shortlisted":
          if (work.selected) {
            newVal = true;
            valid = submission.shortlisted === false;
          } else {
            newVal = submission.shortlisted === false;
            valid = true;
          }
          break;
        case "selected":
          newVal = work.selected === false;
          valid = true;
          break;
        case "longRead":
          newVal = submission.longRead !== true;
          valid = true;
          break;
      }
      if (this.mayMark && valid) {
        reviewSubmission(_id, key, newVal, workId).then(data => {
          if (data.submission) {
            if (data.submission instanceof Object) {
              bus.$emit("slide-update", {
                subId: _id,
                key,
                newVal,
                workId
              });
            }
          }
        });
      }
    }
  }
};
</script>